import {aiAgentRoutes} from '@ai/ai-agent/ai-agent-routes';
import {adminQueries} from '@app/admin/admin-queries';
import {cannedRepliesRoutes} from '@app/canned-replies/canned-replies-routes';
import {customersRoutes} from '@app/dashboard/contacts/customers-routes';
import {helpdeskReportRoutes} from '@app/dashboard/reports/helpdesk-reports-routes';
import {statusesRoutes} from '@app/dashboard/statuses/statuses-routes';
import {teamRoutes} from '@app/dashboard/team-routes';
import {viewsRoutes} from '@app/dashboard/views/views-routes';
import {hcManagerRoutes} from '@app/help-center/manager/hc-manager-routes';
import {adminCustomPagesRoutes} from '@common/admin/custom-pages/admin-custom-pages-routes';
import {adminFileEntriesRoutes} from '@common/admin/file-entry/admin-file-entries-routes';
import {adminLogsRoutes} from '@common/admin/logging/admin-logs-routes';
import {adminRolesRoutes} from '@common/admin/roles/admin-roles-routes';
import {commonAdminSettingsRoutes} from '@common/admin/settings/common-admin-settings-routes';
import {adminBillingRoutes} from '@common/admin/subscriptions/admin-billing-routes';
import {adminTagsRoutes} from '@common/admin/tags/admin-tags-routes';
import {adminLocalizationsRoutes} from '@common/admin/translations/admin-localizations-routes';
import {authGuard} from '@common/auth/guards/auth-route';
import {shouldRevalidateDatatableLoader} from '@common/datatable/filters/utils/should-revalidate-datatable-loader';
import {queryClient} from '@common/http/query-client';
import {campaignsRoutes} from '@livechat/dashboard/campaigns/campaigns-routes';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {searchParamsFromUrl} from '@ui/utils/urls/search-params-from-url';
import {redirect, RouteObject} from 'react-router';
import {Fragment} from 'react/jsx-runtime';

export const adminRoutes: RouteObject[] = [
  {
    path: 'admin',
    loader: () => authGuard({permission: 'admin.access'}),
    lazy: () => import('@common/admin/admin-layout'),
    children: [
      {
        index: true,
        loader: () => redirect('settings'),
        element: <Fragment />,
      },
      ...helpdeskReportRoutes,
      ...teamRoutes,
      ...customersRoutes,
      ...campaignsRoutes,
      ...viewsRoutes,
      ...statusesRoutes,
      ...hcManagerRoutes,
      ...cannedRepliesRoutes(false),
      ...aiAgentRoutes,

      ...Object.values(adminRolesRoutes),
      ...Object.values(adminCustomPagesRoutes),
      ...Object.values(adminTagsRoutes),
      ...Object.values(adminLocalizationsRoutes),
      ...Object.values(adminFileEntriesRoutes),
      ...Object.values(adminLogsRoutes),
      // Subscription/plan administration (plans list + plan editor +
      // subscriptions datatable). Inherited from common/; only mounted on
      // SaaS-enabled domains (settings.billing.integrated). The billing
      // settings page (/admin/settings/subscriptions) is already registered
      // by commonAdminSettingsRoutes and self-reveals in the settings nav.
      ...(getBootstrapData().settings.billing.integrated
        ? Object.values(adminBillingRoutes)
        : []),

      commonAdminSettingsRoutes(
        [
          {
            path: 'tickets',
            lazy: () => import('@app/admin/settings/tickets-settings'),
          },
          {
            path: 'search',
            lazy: () =>
              import(
                '@common/admin/settings/pages/search-settings/search-settings'
              ),
          },
          {
            path: 'envato',
            lazy: () =>
              import('@app/admin/settings/envato-settings/envato-settings'),
          },
          {
            path: 'hc',
            lazy: () => import('@app/admin/settings/hc/hc-settings'),
          },
          {
            path: 'email/incoming',
            lazy: () =>
              import(
                '@common/admin/settings/pages/email-settings/incoming-email/incoming-email-settings'
              ),
          },
          {
            path: 'email/bidirectional',
            lazy: () =>
              import(
                '@common/admin/settings/pages/email-settings/bidirectional-email/bidirectional-email-settings'
              ),
          },
          {
            // Customization (change AA): widgets list + per-widget editor.
            path: 'livechat',
            lazy: () =>
              import(
                '@livechat/admin/settings/chat-widgets/chat-widgets-list-page'
              ),
          },
          {
            path: 'livechat/:widgetId',
            lazy: () => import('@livechat/admin/settings/livechat-settings'),
          },
          {
            path: 'ai',
            lazy: () => import('@ai/admin/settings/ai-settings-page'),
          },
          {
            // Customization (change BB): Translation settings (DeepL API key).
            path: 'translation',
            lazy: () => import('@app/admin/settings/translation-settings'),
          },
          {
            // Slack workspace bot token for agent-DM notifications.
            path: 'slack',
            lazy: () => import('@app/admin/settings/slack-settings'),
          },
          // "Messaging" settings: one tabbed page grouping every messaging
          // channel (WhatsApp / Messenger / Instagram / Telegram / SMS). The
          // index redirects to the first tab; Messenger & Instagram share the
          // meta-messaging page (platform read from the URL).
          {
            path: 'messaging',
            loader: () => redirect('/admin/settings/messaging/whatsapp'),
            element: <Fragment />,
          },
          {
            path: 'messaging/whatsapp',
            lazy: () => import('@app/admin/settings/whatsapp-settings'),
          },
          {
            path: 'messaging/messenger',
            lazy: () => import('@app/admin/settings/meta-messaging-settings'),
          },
          {
            path: 'messaging/instagram',
            lazy: () => import('@app/admin/settings/meta-messaging-settings'),
          },
          {
            path: 'messaging/telegram',
            lazy: () => import('@app/admin/settings/telegram-settings'),
          },
          {
            path: 'messaging/sms',
            lazy: () => import('@app/admin/settings/sms-settings'),
          },
          {
            path: 'messaging/linkedin',
            lazy: () => import('@app/admin/settings/linkedin-settings'),
          },
          // Backward-compat redirects for the old flat channel URLs / bookmarks.
          {
            path: 'whatsapp',
            loader: () => redirect('/admin/settings/messaging/whatsapp'),
            element: <Fragment />,
          },
          {
            path: 'meta-messaging',
            loader: () => redirect('/admin/settings/messaging/messenger'),
            element: <Fragment />,
          },
          {
            path: 'telegram',
            loader: () => redirect('/admin/settings/messaging/telegram'),
            element: <Fragment />,
          },
          {
            path: 'sms',
            loader: () => redirect('/admin/settings/messaging/sms'),
            element: <Fragment />,
          },
        ],
        {
          captcha: {
            lazy: () => import('@app/admin/settings/captcha-settings'),
          },
          system: {
            lazy: () =>
              import(
                '@common/admin/settings/pages/system-settings/system-settings'
              ),
          },
        },
      ),

      // attributes
      {
        path: 'attributes',
        shouldRevalidate: shouldRevalidateDatatableLoader,
        lazy: () =>
          import('@app/attributes/datatable/attributes-datatable-page'),
      },
      {
        path: 'attributes/new',
        lazy: () => import('@app/attributes/crupdate/create-attribute-page'),
      },
      {
        path: 'attributes/:id/edit',
        lazy: () => import('@app/attributes/crupdate/update-attribute-page'),
      },

      // triggers
      {
        path: 'triggers',
        shouldRevalidate: shouldRevalidateDatatableLoader,
        lazy: () => import('@app/triggers/triggers-datatable-page'),
        loader: async ({request}) => {
          const redirect = authGuard({permission: 'triggers.update'});
          if (redirect) return redirect;
          return await queryClient.ensureQueryData(
            adminQueries.triggers.index(searchParamsFromUrl(request.url)),
          );
        },
      },
      {
        path: 'triggers/new',
        lazy: () => import('@app/triggers/create-trigger-page'),
        loader: async () => {
          const redirect = authGuard({permission: 'triggers.update'});
          if (redirect) return redirect;
          return await queryClient.ensureQueryData(
            adminQueries.triggers.config(),
          );
        },
      },
      {
        path: 'triggers/:triggerId/edit',
        lazy: () => import('@app/triggers/update-trigger-page'),
        loader: async ({params}) => {
          const redirect = authGuard({permission: 'triggers.update'});
          if (redirect) return redirect;
          return await Promise.allSettled([
            queryClient.ensureQueryData(
              adminQueries.triggers.get(params.triggerId!),
            ),
            queryClient.ensureQueryData(adminQueries.triggers.config()),
          ]);
        },
      },
      // business-hours calendars
      {
        path: 'business-hours',
        lazy: () =>
          import('@app/business-hours/business-hours-list-page'),
        loader: () => authGuard({permission: 'admin.access'}),
      },
      {
        path: 'business-hours/new',
        lazy: () =>
          import('@app/business-hours/create-business-hours-page'),
        loader: () => authGuard({permission: 'admin.access'}),
      },
      {
        path: 'business-hours/:businessHoursId/edit',
        lazy: () =>
          import('@app/business-hours/update-business-hours-page'),
        loader: () => authGuard({permission: 'admin.access'}),
      },
      // SLA policies
      {
        path: 'sla-policies',
        lazy: () => import('@app/sla/sla-policies-list-page'),
        loader: () => authGuard({permission: 'admin.access'}),
      },
      {
        path: 'sla-policies/new',
        lazy: () => import('@app/sla/create-sla-policy-page'),
        loader: () => authGuard({permission: 'admin.access'}),
      },
      {
        path: 'sla-policies/:slaPolicyId/edit',
        lazy: () => import('@app/sla/update-sla-policy-page'),
        loader: () => authGuard({permission: 'admin.access'}),
      },
    ],
  },
];
